import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DateFormControl } from './date-form-control';
import { ITarjetaInfo } from 'app/services/Models/tarjeta-info';
import { OpenPayService } from 'app/services/openPay-service';
import { SweetAlertMsgService } from 'app/services/sweet-alert-msg.service';

@Component({
    selector: 'app-dialog-tarjeta',
    templateUrl: './dialog-tarjeta.component.html',
    styleUrls: ['./dialog-tarjeta.component.scss'],

})
export class DialogTarjetaComponent implements OnInit {
    tarjetaForm: FormGroup;
    cvvMaxLength: number = 3;
    cardNumberMaxLength: number = 19;
    cardTypeImage: string | null = null;


    // JSON de los BINs
    cardBins = [
        { bins: '1', bank: 'Carnet', image: 'assets/icons/carnet.png' },

        { bins: '2', bank: 'Carnet', image: 'assets/icons/carnet.png' },
        { bins: '2221 ', bank: 'Master Card', image: 'assets/icons/mastercard.png' },
        { bins: '2720 ', bank: 'Master Card', image: 'assets/icons/mastercard.png' },

        { bins: '34', bank: 'American Express', image: 'assets/icons/americanExpress.png' },
        { bins: '37', bank: 'American Express', image: 'assets/icons/americanExpress.png' },

        { bins: '4', bank: 'VISA', image: 'assets/icons/visa.png' },

        { bins: '51', bank: 'Master Card', image: 'assets/icons/mastercard.png' },
        { bins: '55', bank: 'Master Card', image: 'assets/icons/mastercard.png' },

        { bins: '6', bank: 'Carnet', image: 'assets/icons/carnet.png' },
        { bins: '7', bank: 'Carnet', image: 'assets/icons/carnet.png' },
        { bins: '8', bank: 'Carnet', image: 'assets/icons/carnet.png' },
        { bins: '9', bank: 'Carnet', image: 'assets/icons/carnet.png' },
    ];


    constructor(public dialogRef: MatDialogRef<DialogTarjetaComponent>,
        private openPayService: OpenPayService,
        @Inject(MAT_DIALOG_DATA) public data: ITarjetaInfo,
        private _msg: SweetAlertMsgService,
    ) {

    }



    ngOnInit(): void {
        this.tarjetaForm = new FormGroup({
            nombre: new FormControl(this.data?.nombre!, [Validators.required]),
            num: new FormControl(this.data?.numero, [
                Validators.required,
                this.validateNumCardLength.bind(this)
            ]),
            expiration: new DateFormControl(this.data?.expiration, [
                Validators.required,
                Validators.pattern(/^(0[1-9]|1[0-2])?\d{2}$/)
            ]),
            cvv: new FormControl(this.data?.cvv, [
                Validators.required,
                this.validateCVVLength.bind(this)
            ]),
        });

        // Obtener la imagen de sessionStorage en la inicialización
        const storedCardTypeImage = sessionStorage.getItem('cardTypeImage');
        if (storedCardTypeImage) {
            this.cardTypeImage = storedCardTypeImage;
        }

        let cardNumber = this.tarjetaForm.controls.num.value;
        if (cardNumber) {
            cardNumber = cardNumber.replace(/\D/g, '');
            const cardType = this.getCardType(cardNumber);
            if (cardType) {
                const formattedCardNumber = this.formatCardNumber(cardNumber, cardType.bank);
                this.tarjetaForm.get('num')?.setValue(formattedCardNumber, { emitEvent: false });
            } else {
                // Si es de tipo Generic, formatea como tal
                const formattedCardNumber = this.formatCardNumber(cardNumber, 'Generic');
                this.tarjetaForm.get('num')?.setValue(formattedCardNumber, { emitEvent: false });
            }
        }

    }


    getCardType(cardNumber: string): any {
        const bin = cardNumber.slice(0, 2);
        return this.cardBins.find(card => bin.startsWith(card.bins));
    }

    onCardNumberInput() {
        let cardNumber = this.tarjetaForm.controls.num.value;
        if (cardNumber) {
            this.tarjetaForm.get('cvv')?.reset();

            cardNumber = cardNumber.replace(/\D/g, '');

            // Actualiza el valor del campo con solo números
            this.tarjetaForm.get('num')?.setValue(cardNumber, { emitEvent: false });

            const bin = cardNumber.slice(0, 4);
            const cardType = this.cardBins.find(card => bin.startsWith(card.bins));

            if (cardType) {
                this.cardTypeImage = cardType.image;
                this.cvvMaxLength = cardType.bank === 'American Express' ? 4 : 3;

                // Update validators
                this.tarjetaForm.get('cvv')?.setValidators([
                    Validators.required,
                    Validators.minLength(this.cvvMaxLength),
                    Validators.maxLength(this.cvvMaxLength),
                    this.validateCVVLength.bind(this)
                ]);
                this.tarjetaForm.get('cvv')?.updateValueAndValidity();

                this.cardNumberMaxLength = cardType.bank === 'American Express' ? 17 : 19;

                // Update validators
                this.tarjetaForm.get('num')?.setValidators([
                    Validators.required,
                    Validators.minLength(this.cardNumberMaxLength),
                    Validators.maxLength(this.cardNumberMaxLength),
                    this.validateNumCardLength.bind(this)
                ]);
                this.tarjetaForm.get('num')?.updateValueAndValidity();

                // Formatear el número de tarjeta
                const formattedCardNumber = this.formatCardNumber(cardNumber, cardType.bank);
                this.tarjetaForm.get('num')?.setValue(formattedCardNumber, { emitEvent: false });
            } else if (cardNumber.length === 16) { // Si no es un tipo de tarjeta conocido pero tiene 16 dígitos
                this.cardTypeImage = 'assets/icons/tarjeta.png'; // No hay imagen de tipo de tarjeta
                this.cvvMaxLength = 3; // Asumir CVV de 3 dígitos

                // Configura los validadores para el CVV
                this.tarjetaForm.get('cvv')?.setValidators([
                    Validators.required,
                    Validators.minLength(this.cvvMaxLength),
                    Validators.maxLength(this.cvvMaxLength),
                    this.validateCVVLength.bind(this)
                ]);
                this.tarjetaForm.get('cvv')?.updateValueAndValidity();

                // Formatear el número de tarjeta genérico
                const formattedCardNumber = this.formatCardNumber(cardNumber, 'Generic');
                this.tarjetaForm.get('num')?.setValue(formattedCardNumber, { emitEvent: false });
            } else {
                this.resetCVVValidation();
                this.resetNumValidation();
            }
        } else {
            this.resetCVVValidation();
            this.resetNumValidation();
        }
    }

    onCvvInput() {
        let cvv = this.tarjetaForm.controls.cvv.value;


        if (cvv) {
            cvv = cvv.replace(/\D/g, '');

            this.tarjetaForm.get('cvv')?.setValue(cvv, { emitEvent: false });


            const cardNumber = this.tarjetaForm.controls.num.value.replace(/\D/g, '');
            const bin = cardNumber.slice(0, 4);
            const cardType = this.cardBins.find(card => bin.startsWith(card.bins));

            if (cardType) {
                this.cvvMaxLength = cardType.bank === 'American Express' ? 4 : 3;


                this.tarjetaForm.get('cvv')?.setValidators([
                    Validators.required,
                    Validators.minLength(this.cvvMaxLength),
                    Validators.maxLength(this.cvvMaxLength),
                    this.validateCVVLength.bind(this)
                ]);
                this.tarjetaForm.get('cvv')?.updateValueAndValidity();
            }
        } else {

            this.resetCVVValidation();
        }
    }



    validateNumCardLength(control: AbstractControl): ValidationErrors | null {
        //const rawCardNumber = control.value.replace(/\D/g, '');
        const rawCardNumber = control.value;
        const expectedLength = (rawCardNumber.startsWith('34') || rawCardNumber.startsWith('37')) ? 17 : 19;

        if (rawCardNumber.length !== expectedLength) {
            return { numLength: { requiredLength: expectedLength, actualLength: rawCardNumber.length } };
        }
        return null;
    }


    validateCVVLength(control: AbstractControl): ValidationErrors | null {
        const cvv = control.value;

        if (cvv && cvv.length !== this.cvvMaxLength) {
            return { cvvLength: { requiredLength: this.cvvMaxLength, actualLength: cvv.length } };
        }

        return null;
    }


    formatCardNumber(cardNumber: string, bank: string): string {
        if (bank === 'American Express') {
            return cardNumber.replace(/(\d{4})(\d{6})(\d{5})/, '$1-$2-$3');
        } else if (bank === 'Generic') {
            return cardNumber.replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
        } else {
            return cardNumber.replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '$1-$2-$3-$4');
        }
    }

    resetCVVValidation() {
        this.cardTypeImage = null;
        this.cvvMaxLength = 3;

        // Reset CVV validation to default (3 digits)
        this.tarjetaForm.get('cvv')?.setValidators([
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(3),
            this.validateCVVLength.bind(this)
        ]);
        this.tarjetaForm.get('cvv')?.updateValueAndValidity();
    }

    resetNumValidation() {
        this.cardTypeImage = null;
        this.cardNumberMaxLength = 19;

        // Reset Num validation to default (19 digits)
        this.tarjetaForm.get('num')?.setValidators([
            Validators.required,
            Validators.minLength(19),
            Validators.maxLength(19),
            this.validateNumCardLength.bind(this)
        ]);
        this.tarjetaForm.get('num')?.updateValueAndValidity();
    }



    async onSave(): Promise<void> {
        if (this.tarjetaForm.valid) {

            // Almacenar la imagen del tipo de tarjeta en SessionStorage
            sessionStorage.setItem('cardTypeImage', this.cardTypeImage);

            // Obtener los valores del formulario
            let numeroTarjeta = this.tarjetaForm.get('num')?.value;

            // Eliminar los guiones o cualquier otro caracter no numérico
            let numeroTarjetaLimpio = numeroTarjeta.replace(/[^0-9]/g, '');

            console.log("Nombre: ", this.tarjetaForm.get('nombre')?.value);
            console.log("Número sin guiones: ", numeroTarjetaLimpio);
            console.log("Expiration: ", this.tarjetaForm.get('expiration')?.value);
            console.log("CVV: ", this.tarjetaForm.get('cvv')?.value);



            let tar = this.tarjetaForm.value;
            if (Number(tar.expiration.substr(0, 2)) > 12 || Number(tar.expiration.substr(0, 2)) <= 0) {
                this._msg.alertWarning("Atención", "Favor de capturar el mes correcto de mes de vigencia");
                return;
            }
            let year = Number(`${new Date().getFullYear()}`.substr(2, 2));
            if (Number(tar.expiration.substr(2, 2)) < year || (Number(tar.expiration.substr(0, 2)) < (new Date().getMonth() + 1) && year === Number(tar.expiration.substr(2, 2)))) {
                this._msg.alertWarning("Atención", "La fecha de vigencia de la tarjeta ya expiró.");
                return;
            }

            this.dialogRef.close({
                nombre: this.tarjetaForm.value.nombre,
                numero: numeroTarjetaLimpio,
                expiration: this.tarjetaForm.value.expiration,
                cvv: this.tarjetaForm.value.cvv,
                isValid: true
            });

        }
    }

    close(): void {
        this.dialogRef.close({

        });
    }

    onNoClick(): void {
        this.dialogRef.close({

        });
    }

}
